// Client-side pay-period math, mirroring src/server/services/payPeriod.ts.
// Periods are anchored to 2024-01-01 (UTC) and default to biweekly (14-day)
// windows. The server is the source of truth for the configured length; this
// helper provides sensible defaults for the date pickers before a run.

export interface PayPeriod {
    start: string;
    end: string;
}

const ANCHOR = Date.UTC(2024, 0, 1); // 2024-01-01
const DAY_MS = 86_400_000;

function toIso(ms: number): string {
    return new Date(ms).toISOString().slice(0, 10);
}

function startOfUtcDay(ref: Date): number {
    return Date.UTC(ref.getUTCFullYear(), ref.getUTCMonth(), ref.getUTCDate());
}

/** Period (start/end inclusive) of `periodDays` containing `ref`, anchored to ANCHOR. */
export function computePayPeriod(periodDays = 14, ref: Date = new Date()): PayPeriod {
    const today = startOfUtcDay(ref);
    const diffDays = Math.floor((today - ANCHOR) / DAY_MS);
    const periodNum = Math.floor(diffDays / periodDays);
    const startMs = ANCHOR + periodNum * periodDays * DAY_MS;
    const endMs = startMs + (periodDays - 1) * DAY_MS;
    return { start: toIso(startMs), end: toIso(endMs) };
}

/** Current biweekly period (default). */
export function currentPayPeriod(periodDays = 14): PayPeriod {
    return computePayPeriod(periodDays);
}

/** The period immediately before the current one. */
export function previousPayPeriod(periodDays = 14): PayPeriod {
    const cur = computePayPeriod(periodDays);
    const prevRef = new Date(Date.parse(cur.start + "T00:00:00Z") - DAY_MS);
    return computePayPeriod(periodDays, prevRef);
}
